import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { TopBar, DestinationRow, SecondaryAction } from "./kit";
import LoadFailedCard from "./pickxi/LoadFailedCard";
import GroundCard from "./GroundCard";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import api from "../lib/api";
import { getMyGame, gameDateLabel, myLine, tossLabel } from "../lib/coachGames";
import { COACH_GAME_CHIP, clock12 } from "../lib/comingUp";

/**
 * Issue #1319 (Pick XI player; D300, D302, D315, D316 Q7) — a coach's game on
 * the player's side of Plan, `PlannedGameFromCoach.dc.html` `game-page`. A step
 * of `/development` (`?game=<id>`, the way `?plan=match` is), mounted by
 * `pages/DevelopmentPlan.jsx`; there is no new route.
 *
 * TRUST PATH. This is the first screen that shows a player other players'
 * names, so it draws only what `GET /api/coach-games/mine/:id` (#1315) sends,
 * and reads it by named field: no spread, no `Object.entries`, no pass-through
 * of a row. A field the API adds later therefore cannot appear here by being
 * there. Teammates are a name and the C / VC / WK tags (D315). There is no
 * volunteer (D300), no draft and no change made on the day (Q7) in the response,
 * and none is asked for.
 *
 * Read-only: the coach owns the game. A section whose data is null is absent,
 * not an empty box. Loading, failed and ready are three separate states (D53).
 * The ground opens `GroundCard` in place when the API sends a card id for it
 * (`ground_id_for_card`), and is plain text otherwise.
 */

/** The announcement day as the player's own calendar day: `Sat 17 Oct`. */
function announcedLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return gameDateLabel(local);
}

const present = (v) => typeof v === "string" && v.trim() !== "";

function Card({ testId, heading, children }) {
  return (
    <section
      data-testid={testId}
      className="flex flex-col"
      style={{ gap: 8, padding: 16, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      <div className="nw-section">{heading}</div>
      {children}
    </section>
  );
}

function Block({ label, text }) {
  if (!present(text)) return null;
  return (
    <div className="flex flex-col" style={{ gap: 2 }}>
      <div className="nw-label">{label}</div>
      <div className="nw-body" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {text}
      </div>
    </div>
  );
}

function TeamRow({ testId, place, name, tags = [], isMe, suffix }) {
  return (
    <li
      data-testid={testId}
      className="flex items-baseline"
      style={{ gap: 10, minHeight: 32, listStyle: "none", fontWeight: isMe ? 700 : 400 }}
    >
      <span className="nw-label" style={{ width: 22, flexShrink: 0 }}>
        {place}
      </span>
      <span className="nw-body" style={{ minWidth: 0, overflowWrap: "anywhere", fontWeight: isMe ? 700 : 400 }}>
        {name}
        {tags.length > 0 && <span className="nw-help"> {tags.join(" ")}</span>}
        {suffix && <span className="nw-help"> {suffix}</span>}
        {isMe && <span className="nw-help"> {"· you"}</span>}
      </span>
    </li>
  );
}

export default function PlanCoachGame({ gameId, onBack }) {
  const { setActive: setChromeless } = useChromelessOverride();
  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, setState] = useState({ status: "loading", game: null });
  const [cardOpen, setCardOpen] = useState(false);
  const [savedIds, setSavedIds] = useState(() => new Set());

  const load = useCallback(() => {
    let live = true;
    setState({ status: "loading", game: null });
    getMyGame(gameId)
      .then((game) => live && setState({ status: "ready", game }))
      .catch((e) => {
        console.error("PlanCoachGame: failed to load the game", e);
        if (live) setState({ status: "failed", game: null });
      });
    return () => {
      live = false;
    };
  }, [gameId]);

  useEffect(() => load(), [load]);

  // The ground card's Save button needs the player's saved grounds, as it does
  // on the Grounds page. Read only when the card opens.
  useEffect(() => {
    if (!cardOpen) return undefined;
    let live = true;
    api
      .get("/players/me/grounds")
      .then((r) => live && setSavedIds(new Set((r.data || []).map((x) => x.ground_id))))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [cardOpen]);

  const toggleSaved = async (g, e) => {
    e?.stopPropagation?.();
    try {
      if (savedIds.has(g.ground_id)) {
        await api.delete(`/players/me/grounds/${g.ground_id}`);
        setSavedIds((prev) => {
          const n = new Set(prev);
          n.delete(g.ground_id);
          return n;
        });
        toast.success(`Removed "${g.name}" from My Grounds`);
      } else {
        await api.post("/players/me/grounds", { ground_id: g.ground_id });
        setSavedIds((prev) => new Set(prev).add(g.ground_id));
        toast.success(`Added "${g.name}" to My Grounds`);
      }
    } catch {
      toast.error("Could not update My Grounds");
    }
  };

  const game = state.game;

  if (cardOpen && game?.ground_id_for_card) {
    return (
      <GroundCard
        groundId={game.ground_id_for_card}
        hint={{ ground_id: game.ground_id_for_card, name: game.ground }}
        savedIds={savedIds}
        onToggleSaved={toggleSaved}
        onBack={() => setCardOpen(false)}
      />
    );
  }

  const chrome = (
    <>
      <TopBar
        variant="white"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="plan-game-topbar"
      />
      <DestinationRow current="plan" />
    </>
  );

  const back = (
    <SecondaryAction
      variant="link"
      data-testid="game-back"
      onClick={onBack}
      style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}
    >
      <ChevronLeft size={16} aria-hidden="true" />
      Back to Plan
    </SecondaryAction>
  );

  const shell = (body) => (
    <div data-testid="game-page" style={{ color: "var(--nw-ink)" }}>
      {chrome}
      <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
        {back}
        {body}
      </div>
    </div>
  );

  if (state.status === "loading") {
    return shell(
      <div data-testid="game-loading" aria-busy="true" className="flex flex-col" style={{ gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 76, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }} />
        ))}
      </div>,
    );
  }

  if (state.status === "failed" || !game) {
    return shell(<LoadFailedCard title="Couldn't load this game" testId="game-failed" retryTestId="game-failed-retry" onRetry={load} />);
  }

  // Named fields only (D300, D315). Nothing below spreads or iterates `game`.
  const team = game.team;
  const plan = game.plan;
  const notes = game.notes;
  const coach = present(game.coach_name) ? game.coach_name : "Your coach";
  const line = myLine(team);
  const place = team?.me?.place;
  const meTestId = place === "twelfth" ? "game-me-twelfth" : place === "bench" ? "game-me-bench" : "game-me";
  const when = [
    [gameDateLabel(game.match_date), clock12(game.match_time)].filter(Boolean).join(" · "),
    game.competition,
    game.team_name,
  ]
    .filter(present)
    .join(" · ");
  const groundText = [game.ground, game.ground_city].filter(present).join(" ");
  const remember = (plan?.remember || []).filter(present);
  const toss = tossLabel(plan?.toss);
  const planHasContent = !!plan && (toss || present(plan.plan_bat) || present(plan.plan_ball) || present(plan.plan_opposition));
  const arrive = clock12(notes?.arrive_by);
  const notesHaveContent = !!notes && (arrive || present(notes.team_notes));
  const openers = (team?.opening_bowlers || []).filter(present);

  return shell(
    <>
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="nw-eyebrow">{COACH_GAME_CHIP}</div>
        <h1 className="nw-title-lg" data-testid="game-title" style={{ margin: 0 }}>
          {`Match vs ${game.opponent}`}
        </h1>
        {when && (
          <div className="nw-lede" data-testid="game-when">
            {when}
          </div>
        )}
      </div>

      {present(game.ground) &&
        (game.ground_id_for_card ? (
          <button
            type="button"
            data-testid="game-ground"
            onClick={() => setCardOpen(true)}
            className="flex items-center justify-between"
            style={{
              width: "100%",
              minHeight: 44,
              padding: "0 14px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-control)",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontSize: 15,
              color: "var(--nw-ink)",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <span>{groundText}</span>
            <ChevronRight size={18} aria-hidden="true" style={{ color: "var(--nw-ink-faint)", flexShrink: 0 }} />
          </button>
        ) : (
          <div data-testid="game-ground" className="nw-body">
            {groundText}
          </div>
        ))}

      {team ? (
        <Card testId="game-team" heading="The team">
          {announcedLabel(game.announced_at) && <div className="nw-help">{`Announced ${announcedLabel(game.announced_at)}`}</div>}
          {line && (
            <div data-testid={meTestId} className="nw-body-strong">
              {line}
            </div>
          )}
          {(team.xi || []).length > 0 && (
            <ol className="flex flex-col" style={{ margin: 0, padding: 0, gap: 2 }}>
              {team.xi.map((r) => (
                <TeamRow
                  key={`xi-${r.bat_order}`}
                  testId={`game-team-row-${r.bat_order}`}
                  place={r.bat_order}
                  name={r.name}
                  tags={[r.captain && "C", r.vice_captain && "VC", r.keeper && "WK"].filter(Boolean)}
                  isMe={!!r.is_me}
                />
              ))}
              {team.twelfth && (
                <TeamRow
                  testId="game-team-twelfth"
                  place="12"
                  name={team.twelfth.name}
                  suffix={"12th man · fields"}
                  isMe={!!team.twelfth.is_me}
                />
              )}
            </ol>
          )}
          {openers.length > 0 && (
            <div className="nw-body" data-testid="game-team-openers">
              {`Opening bowlers: ${openers.join(", ")}`}
            </div>
          )}
        </Card>
      ) : (
        <div
          data-testid="game-team-pending"
          className="nw-body"
          style={{ padding: 16, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
        >
          {`You're in the squad for this game. ${coach} hasn't announced the team yet.`}
        </div>
      )}

      {remember.length > 0 && (
        <Card testId="game-three" heading="Three things to remember">
          <ol className="flex flex-col" style={{ margin: 0, padding: 0, gap: 6 }}>
            {remember.map((t, i) => (
              <li key={i} className="flex items-baseline" style={{ gap: 10, listStyle: "none" }}>
                <span className="nw-label" style={{ width: 14, flexShrink: 0 }}>
                  {i + 1}
                </span>
                <span className="nw-body" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {t}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {planHasContent && (
        <Card testId="game-plan" heading="Game plan">
          {toss && <div className="nw-body-strong" data-testid="game-plan-toss">{`If we win the toss: ${toss}`}</div>}
          <Block label="With the bat" text={plan.plan_bat} />
          <Block label="With the ball & in the field" text={plan.plan_ball} />
          <Block label="About the opposition" text={plan.plan_opposition} />
        </Card>
      )}

      {notesHaveContent && (
        <Card testId="game-notes" heading="General notes">
          {arrive && <div className="nw-body-strong">{`Arrive by ${arrive}`}</div>}
          {present(notes.team_notes) && (
            <div className="nw-body" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {notes.team_notes}
            </div>
          )}
        </Card>
      )}
    </>,
  );
}
